import type { ComponentProps } from 'react';
import {
  cn,
  fromMinorUnit,
  currencySymbol,
  formatIntegerWithCommas,
  type Money,
} from '@shared/lib';

type Tone = 'auto' | 'positive' | 'negative' | 'neutral';
type Weight = 'normal' | 'medium' | 'semibold' | 'bold';

const toneClass: Record<Exclude<Tone, 'auto'>, string> = {
  positive: 'text-positive',
  negative: 'text-negative',
  neutral: 'text-fg',
};

const weightClass: Record<Weight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export interface AmountProps extends Omit<ComponentProps<'span'>, 'children'> {
  /** Branded minor-unit value (see `@shared/lib` money). */
  value: Money;
  /** ISO 4217 currency code, e.g. "USD", "INR". */
  currency: string;
  /** Override the currency-symbol locale (defaults to "en-US" — see `activeLocale` below). */
  locale?: string;
  isTabular?: boolean;
  /** `auto` colours by sign; otherwise force a tone. */
  tone?: Tone;
  weight?: Weight;
  /** Prefix positive values with a "+" (for "you will receive" displays). */
  showPlus?: boolean;
  /** Whether to show the fractional part in a smaller font size. */
  showFracPartSmaller?: boolean;
}

/**
 * Currency amount. Formats branded `Money` with the shared `fromMinorUnit` helper (matching the
 * Flutter app: fixed 2 decimals) formatted with comma grouping plus a locale currency symbol, and colours
 * by sign (green positive / red negative). `tabular-nums` keeps columns aligned.
 */
export function Amount({
  value,
  currency,
  locale,
  isTabular = true,
  tone = 'auto',
  weight = 'medium',
  showPlus = false,
  showFracPartSmaller = false,
  className,
  ...props
}: AmountProps) {
  const resolved: Exclude<Tone, 'auto'> =
    tone === 'auto' ? (value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral') : tone;

  // Pin to en-US by default (not navigator.language) — Intl.NumberFormat renders USD as "US$" in
  // most non-US locales, which would show e.g. "US$5595.68" instead of "$5595.68" for such viewers.
  const activeLocale = locale ?? 'en-US';
  const symbol = currencySymbol(currency, activeLocale);
  const [rawIntPart, fracPart] = fromMinorUnit(Math.abs(value)).split('.');
  const intPart = formatIntegerWithCommas(rawIntPart ?? '0');
  const sign = value < 0 ? '-' : showPlus && value > 0 ? '+' : '';
  const tabularClass = isTabular ? 'tabular-nums' : '';

  return (
    <span
      data-slot="amount"
      className={cn(
        'inline-flex items-baseline',
        toneClass[resolved],
        weightClass[weight],
        tabularClass,
        className,
      )}
      {...props}
    >
      {sign ? <span>{sign}</span> : null}
      <span data-slot="amount-symbol" className="font-sans text-xs font-normal mr-0.5">
        {symbol}
      </span>
      <span>{intPart}</span>
      {fracPart ? (
        <span className={showFracPartSmaller ? 'text-[0.65em] font-normal' : undefined}>
          .{fracPart}
        </span>
      ) : null}
    </span>
  );
}
